<template>
  <div>
    <TieredMenu
      ref="rowMenu"
      id="case_row_actions_menu"
      :model="rowMenuItems"
      :popup="true"
      class="text-sm"
      :key="rowMenuRowSelected"
    />
    <DataTable
      :value="displayData"
      :paginator="!showSkeletonLoading"
      :rows="props.pagination ? props.pagination?.pageSize || 20 : 20"
      :rowsPerPageOptions="[10, 20, 50, 100]"
      :sortable="!showSkeletonLoading"
      :globalFilterFields="['CASE_ID', 'MERCHANT_ID', 'JIRA_ISSUE.ISSUE_KEY']"
      class="p-datatable-sm"
      stripedRows
      hover
      responsiveLayout="scroll"
      scrollable
      scrollHeight="calc(100vh - 25rem)"
      sortField="CREATED_AT"
      columnResizeMode="expand"
      :sortOrder="-1"
      :totalRecords="
        props.pagination ? props.pagination?.total || 0 : undefined
      "
      :lazy="!!props.pagination"
      @page="onPageChange"
      @rows-change="onRowsChange"
      @row-click="onRowClick"
      data-test-id="fcc-cases-table"
    >
      <Column
        field="CASE_ID"
        header="Case"
        sortable
        style="width: 12rem; min-width: 10rem"
      >
        <template #body="{ data }">
          <div v-if="showSkeletonLoading" class="flex items-center gap-2">
            <Skeleton width="2rem" height="1rem"></Skeleton>
            <Skeleton
              width="6rem"
              height="1.5rem"
              class="rounded-full"
            ></Skeleton>
          </div>
          <div v-else class="flex items-center gap-2">
            <span
              class="text-sm font-medium text-surface-900 dark:text-surface-0"
            >
              #{{ data.CASE_ID }}
            </span>
            <div :data-test-id="`case-status-badge-${data.CASE_ID}`">
              <BadgeFccSeverity
                :background-color="
                  data?.JIRA_ISSUE?.RESOLVED_AT
                    ? 'bg-green-50 dark:bg-green-900/20'
                    : 'bg-surface-100 dark:bg-surface-800'
                "
                :text-color="
                  data?.JIRA_ISSUE?.RESOLVED_AT
                    ? 'text-green-700 dark:text-green-200'
                    : 'text-surface-600 dark:text-surface-300'
                "
                :border-color="
                  data?.JIRA_ISSUE?.RESOLVED_AT
                    ? 'border-green-500/40 dark:border-green-700/60'
                    : 'border-surface-500/40 dark:border-surface-700/60'
                "
              >
                <div class="flex items-center gap-1">
                  <LockClosedIcon
                    v-if="data?.JIRA_ISSUE?.RESOLVED_AT"
                    class="size-4"
                  />
                  <LockOpenIcon v-else class="size-4" />
                  <span>{{
                    data?.JIRA_ISSUE?.RESOLVED_AT ? 'Resolved' : 'Open'
                  }}</span>
                </div>
              </BadgeFccSeverity>
            </div>
          </div>
        </template>
      </Column>

      <Column
        v-if="props.showMerchantColumn"
        field="MERCHANT_ID"
        header="Supplier"
        sortable
        style="width: 12rem"
      >
        <template #body="{ data }">
          <div v-if="showSkeletonLoading">
            <Skeleton width="8rem" height="1rem" class="mb-2"></Skeleton>
            <Skeleton width="4rem" height="0.75rem"></Skeleton>
          </div>
          <div
            v-else
            class="flex flex-col w-48 text-xs text-surface-700 dark:text-surface-200"
          >
            <button
              type="button"
              class="text-sm font-medium text-surface-900 dark:text-surface-0 text-left hover:underline underline-offset-[3px] decoration decoration-primary-500 dark:decoration-primary-400 cursor-pointer"
              :title="getMerchantName(data.MERCHANT_ID)"
              @click.stop="viewMerchantDetails(data.MERCHANT_ID)"
              :data-test-id="`case-merchant-btn-${data.CASE_ID}`"
            >
              {{ getMerchantName(data.MERCHANT_ID) }}
            </button>
            <div class="text-xs text-surface-500 dark:text-surface-400">ID: {{ data.MERCHANT_ID }}</div>
          </div>
        </template>
      </Column>

      <Column
        field="OPENING_ALERT_ID"
        header="Opening Alert"
        sortable
        style="min-width: 9rem"
      >
        <template #body="{ data }">
          <div v-if="showSkeletonLoading">
            <Skeleton width="8rem" height="1rem" class="mb-2"></Skeleton>
            <Skeleton width="4rem" height="0.75rem"></Skeleton>
          </div>
          <div
            v-else-if="data.OPENING_ALERT_ID"
            class="flex text-xs text-surface-700 dark:text-surface-200 w-full"
          >
            <!-- -ms cancels the button's own padding so the type lines up with
                 the header; justify-start stops a wrapped label centring -->
            <Button
              variant="text"
              size="small"
              class="-ms-2.5 !justify-start text-left"
              @click.stop="openAlertDetails(data.OPENING_ALERT_ID)"
            >
              <div class="flex flex-col gap-1 items-start">
                <div
                  class="text-sm font-medium"
                  :data-test-id="`case-alert-type-${data.CASE_ID}`"
                >
                  {{ getAlertName(data.OPENING_ALERT_ID)
                  }}<span class="whitespace-nowrap"
                    >&nbsp;<ArrowTopRightOnSquareIcon
                      class="size-4 inline-block align-text-bottom"
                  /></span>
                </div>
                <!-- Each part stays whole, so a narrow column wraps them onto
                     two lines rather than splitting "ID:" from its number -->
                <div
                  class="flex flex-wrap items-center gap-x-2 text-surface-600 dark:text-surface-300"
                >
                  <span
                    class="text-xs whitespace-nowrap text-surface-600 dark:text-surface-300"
                    >ID: {{ data.OPENING_ALERT_ID }}</span
                  >
                  <span class="text-xs font-medium whitespace-nowrap">
                    {{ getAlertSignificanceFormatted(data.OPENING_ALERT_ID) }}
                  </span>
                </div>
              </div>
            </Button>
          </div>
          <span v-else class="text-sm text-surface-400 dark:text-surface-500">-</span>
        </template>
      </Column>

      <Column
        field="JIRA_ISSUE.ASSIGNEE_NAME"
        header="Assignee"
        sortable
        style="min-width: 8rem"
      >
        <template #body="{ data }">
          <div v-if="showSkeletonLoading">
            <Skeleton width="8rem" height="1rem" class="mb-2"></Skeleton>
            <Skeleton width="10rem" height="0.75rem"></Skeleton>
          </div>
          <div
            v-else
            class="text-sm text-surface-700 dark:text-surface-200 w-full"
          >
            {{ data?.JIRA_ISSUE?.ASSIGNEE_NAME || '-' }}
          </div>
        </template>
      </Column>
      <Column
        field="JIRA_ISSUE.ISSUE_KEY"
        header="JIRA"
        sortable
        style="width: 10rem"
      >
        <template #body="{ data }">
          <div v-if="showSkeletonLoading">
            <Skeleton width="6rem" height="1rem"></Skeleton>
          </div>
          <div v-else>
            <a
              v-if="data.JIRA_ISSUE?.ISSUE_KEY"
              :href="data.JIRA_ISSUE.ISSUE_URL"
              target="_blank"
              rel="noopener noreferrer"
              class="text-sm text-rch-black dark:text-surface-300 hover:text-surface-800 dark:hover:text-surface-200 underline flex gap-2"
              @click.stop
              :data-test-id="`jira-link-${data.JIRA_ISSUE.ISSUE_KEY}`"
            >
              {{ data.JIRA_ISSUE.ISSUE_KEY }}
              <ArrowTopRightOnSquareIcon class="size-4" />
            </a>
            <span v-else class="text-sm text-surface-400 dark:text-surface-500">-</span>
          </div>
        </template>
      </Column>
      <Column
        field="UPDATED_AT"
        header="Dates"
        sortable
        style="width: 16rem"
        alignFrozen="right"
      >
        <template #body="{ data }">
          <div v-if="showSkeletonLoading">
            <div class="flex flex-col gap-1 items-start">
              <Skeleton width="8rem" height="1rem" class="mb-1"></Skeleton>
              <Skeleton width="8rem" height="0.875rem"></Skeleton>
            </div>
          </div>
          <div
            v-else
            class="flex flex-col gap-1 items-start text-surface-600 dark:text-surface-300"
          >
            <div class="text-sm">
              <span class="font-medium text-surface-700 dark:text-surface-200">Updated:</span>
              <span class="ml-1">{{
                formatDateTimeForDisplay(data.UPDATED_AT)
              }}</span>
            </div>
            <div class="text-sm">
              <span class="font-medium text-surface-500 dark:text-surface-400">Created:</span>
              <span class="ml-1">{{
                formatDateTimeForDisplay(data.CREATED_AT)
              }}</span>
            </div>
          </div>
        </template>
      </Column>

      <Column
        v-if="canEdit"
        header="Actions"
        :exportable="false"
        style="width: 4rem"
        class="text-sm font-medium text-surface-600 dark:text-surface-300 border-l border-surface-200 dark:border-surface-700 text-center"
        frozen
        alignFrozen="right"
      >
        <template #body="{ data }">
          <div
            v-if="showSkeletonLoading"
            class="flex justify-center items-center"
          >
            <Skeleton shape="circle" size="2rem"></Skeleton>
          </div>
          <div v-else class="flex justify-center items-center">
            <Button
              icon="pi pi-ellipsis-v"
              rounded
              variant="outlined"
              aria-label="Row Actions"
              @click.prevent="toggleRowMenu($event, data)"
              :data-test-id="`case-actions-btn-${data.CASE_ID}`"
            />
          </div>
        </template>
      </Column>
    </DataTable>

    <AlertDetails
      :visible="alertDetailsVisible"
      :alert="selectedAlert"
      @update:visible="alertDetailsVisible = $event"
    />
  </div>
</template>

<script setup lang="ts">
import { computed, ref, nextTick } from 'vue'
import { useRouter } from 'vue-router'
import DataTable from 'primevue/datatable'
import Column from 'primevue/column'
import Button from 'primevue/button'
import Skeleton from 'primevue/skeleton'
import TieredMenu from 'primevue/tieredmenu'
import BadgeFccSeverity from '@/components/common/badges/BadgeFccSeverity.vue'
import { Case, Alert } from '@/types/fcc'
import { formatDateTimeForDisplay } from '@/util/dateUtils'
import { useFCCAlertStore } from '@/stores/fcc/fccAlertStore'
import { useFCCMerchantStore } from '@/stores/fcc/fccMerchantStore'
import AlertDetails from '@/components/fcc/alerts/AlertDetails.vue'
import { useFCCFormatting } from '@/util/fcc/useFCCFormatting'
import {
  ArrowTopRightOnSquareIcon,
  LockClosedIcon,
  LockOpenIcon,
} from '@heroicons/vue/24/outline'
import { usePerms } from '@/composables/usePerms'
import { PERMISSIONS } from '@/config/permissions'

interface Props {
  casesData?: Case[]
  showMerchantColumn?: boolean
  pagination?: {
    page: number
    pageSize: number
    total: number
    totalPages: number
  }
  paginationLoading?: boolean
  initialLoading?: boolean
}

const props = withDefaults(defineProps<Props>(), {
  showMerchantColumn: true,
  paginationLoading: false,
  initialLoading: false,
})

// Pagination is driven via the FCC case store to avoid emits
import { useFCCCaseStore } from '@/stores/fcc'
const caseStore = useFCCCaseStore()

const router = useRouter()
const { hasPerms } = usePerms()
const alertStore = useFCCAlertStore()
const merchantStore = useFCCMerchantStore()
const { formatEventType, formatSignificance } = useFCCFormatting()

const canEdit = computed(() => hasPerms(PERMISSIONS.FCC_UPDATE))

const alertDetailsVisible = ref(false)
const selectedAlert = ref<Alert | null>(null)

// Menu state for per-row actions
const rowMenu = ref(null)
const rowMenuItems = ref<any[]>([])
const rowMenuRowSelected = ref<PropertyKey | undefined>(undefined)

const showSkeletonLoading = computed(
  () => props.initialLoading || props.paginationLoading,
)

const displayData = computed(() => {
  if (showSkeletonLoading.value) {
    // Return skeleton data for loading state
    return Array.from({ length: 10 }, (_, i) => ({
      CASE_ID: i,
      CREATED_AT: '',
      JIRA_ISSUE_ID: 0,
      MERCHANT_ID: 0,
      OPENING_ALERT_ID: 0,
      SNAPSHOT_ID: 0,
      RESOLVED_AT: null,
      UPDATED_AT: '',
      JIRA_ISSUE: {
        ISSUE_ID: 0,
        ISSUE_KEY: '',
        ISSUE_URL: '',
        SUMMARY: '',
        DESCRIPTION: '',
        STATUS: '',
        ASSIGNEE_NAME: '',
        ASSIGNEE_EMAIL: '',
        IS_OPEN: false,
        CREATED_AT: '',
        UPDATED_AT: '',
        RESOLVED_AT: '',
      },
    }))
  }
  return props.casesData || []
})

function onPageChange(event: any) {
  caseStore.setPagination({ page: event.page + 1, pageSize: event.rows })
}

function onRowsChange(event: any) {
  caseStore.setPagination({ pageSize: event.value })
}

function onRowClick(event: any) {
  // Skeleton rows carry fabricated CASE_IDs — never navigate while loading
  if (showSkeletonLoading.value) return
  const caseData = event.data
  if (caseData && caseData.CASE_ID) {
    navigateToCaseDetails(caseData.CASE_ID)
  }
}

function navigateToCaseDetails(caseId: number) {
  router.push({ name: 'fccCaseDetails', params: { id: caseId.toString() } })
}

async function toggleRowMenu(event: Event, rowData: Case) {
  if (showSkeletonLoading.value) return

  rowMenuRowSelected.value = rowData.CASE_ID ?? undefined
  rowMenuItems.value = buildRowMenuItems(rowData)
  await nextTick()
  if (rowMenu.value) {
    ;(rowMenu.value as any).toggle(event)
  }
}

function buildRowMenuItems(rowData: Case) {
  const items: any[] = []

  items.push({
    label: 'View Case',
    icon: 'pi pi-eye',
    command: () => navigateToCaseDetails(rowData.CASE_ID),
  })

  items.push({
    label: 'Supplier Details',
    icon: 'pi pi-briefcase',
    command: () => viewMerchantDetails(rowData.MERCHANT_ID),
  })

  items.push({
    label: 'Alert Details',
    icon: 'pi pi-bell',
    disabled: !rowData.OPENING_ALERT_ID,
    command: () =>
      rowData.OPENING_ALERT_ID && openAlertDetails(rowData.OPENING_ALERT_ID),
  })

  return items
}

function viewMerchantDetails(merchantId: number) {
  if (!merchantId && merchantId !== 0) return
  router.push({ name: 'fccMerchantDetails', params: { id: merchantId } })
}

function getAlertName(alertId: number): string {
  const alert = alertStore.alerts.find(a => a.ALERT_ID === alertId)
  if (alert) {
    return formatEventType(alert.EVENT_TYPE)
  }
  return `Alert ${alertId}`
}

function getAlertSignificanceFormatted(alertId: number): string {
  const alert = alertStore.alerts.find(a => a.ALERT_ID === alertId)
  if (
    !alert ||
    alert.SIGNIFICANCE === undefined ||
    alert.SIGNIFICANCE === null
  ) {
    return '-'
  }
  return formatSignificance(alert.SIGNIFICANCE)
}

function openAlertDetails(alertId: number) {
  const alert = alertStore.alerts.find(a => a.ALERT_ID === alertId)
  if (!alert) return

  selectedAlert.value = alert
  alertDetailsVisible.value = true
}

function getMerchantName(merchantId: number): string {
  const merchant = merchantStore.getMerchantById(merchantId)
  if (merchant?.MERCHANT_NAME) {
    return merchant.MERCHANT_NAME
  }
  return `Merchant ${merchantId}`
}
</script>

<style scoped>
:deep(.p-datatable-tbody > tr) {
  cursor: pointer;

  &:hover {
    filter: brightness(0.98);
  }
}
</style>
